import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Pin a scroll container to the bottom while new content arrives, but
 * stop pinning if the user scrolls up — they're reading history.
 *
 * Returns:
 *  - `ref` — a CALLBACK ref for the scroll container. It's a callback (not a
 *    ref object) so the scroll listener re-binds whenever the node changes —
 *    e.g. when a transcript swaps its empty-state element for the message list.
 *    A plain ref-object listener bound once on mount would stay attached to the
 *    stale node and `atBottom` would freeze.
 *  - `atBottom` — whether the container is pinned to the bottom (host can show
 *    a "jump to latest" affordance when false),
 *  - `scrollToBottom` — smoothly scroll to the latest and re-enable pinning.
 *
 * Pass any deps that grow content (messages, status) to re-evaluate.
 */
export function useAutoScroll<T extends HTMLElement>(deps: ReadonlyArray<unknown>) {
  const elRef = useRef<T | null>(null)
  const stickRef = useRef(true)
  const [atBottom, setAtBottom] = useState(true)

  const recompute = useCallback(() => {
    const el = elRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    const stuck = distanceFromBottom < 24
    stickRef.current = stuck
    setAtBottom(stuck)
  }, [])

  const ref = useCallback(
    (node: T | null) => {
      if (elRef.current) elRef.current.removeEventListener("scroll", recompute)
      elRef.current = node
      if (node) {
        node.addEventListener("scroll", recompute, { passive: true })
        recompute()
      }
    },
    [recompute],
  )

  // On content growth: keep the view pinned if the user hasn't scrolled away,
  // then recompute so `atBottom` reflects reality even without a scroll event.
  useEffect(() => {
    const el = elRef.current
    if (!el) return
    if (stickRef.current) el.scrollTop = el.scrollHeight
    recompute()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  const scrollToBottom = useCallback(() => {
    const el = elRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" })
    stickRef.current = true
    setAtBottom(true)
  }, [])

  return { ref, atBottom, scrollToBottom }
}
